import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { EventLog, EventLogInfo, GeoMapItem, Sensor, SensorReadingCriteria, SensorReadingCriteria2, SensorReadingType, TimeZoneDisplay as TimeZoneSelection } from '@models';
import { ChartInfo } from '@models/Dashboard/chartData.model';
import { EventLogSearchCriteria } from '@models/EventLog/EventLogSearchCriteria.model';
import { SensorReadingInfo } from '@models/EventLog/SensorReadingInfo.model';
import { DateRanges } from '@models/ranges';
import { SensorService } from '@services/sensor.service';
import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import * as d3 from 'd3';
import { TableLazyLoadEvent } from 'primeng/table';
import { GpsInfo } from '@models/Map/gpsInfo.model';
import { GoogleMap } from '@angular/google-maps';
import { KigSettings, KIG_SETTINGS } from 'settings/kig-settings';
import { ExportDataModalComponent } from '../export-data-modal/export-data-modal.component';
import { CommonShared } from 'app/shared/classes/common';


interface DateRange {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

export interface SensorDataModalParams {
 sensor: Sensor;
 selectedTimeZone: TimeZoneSelection;
 selectedTimeZoneOffset: number;
 selectedDateRange: DateRange;
 selectedDefaultReadingType: string;
}

const selectedRects = {startRect: null, endRect: null};

@Component({
  standalone: false,
  selector: 'app-sensor-data-modal',
  templateUrl: './sensor-data-modal.component.html',
  styleUrls: ['./sensor-data-modal.component.scss']
})

export class SensorDataModalComponent implements OnInit, SensorDataModalParams {

  @ViewChild('googleMap', { static: false })
    map!: GoogleMap;

  @ViewChild(ExportDataModalComponent) exportDataModal: ExportDataModalComponent;

  common = new CommonShared();

  modalDragOn: boolean = false;

  mapZoom = 13;
  mapCenter!: google.maps.LatLngLiteral;
  mapOptions: google.maps.MapOptions = {
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: true,
    mapTypeId: 'hybrid',
    maxZoom: 21,
    minZoom: 1,
  };
  // One pin per "visit": consecutive readings sharing the same coordinates are
  // grouped together, so a mobile sensor returning to an earlier spot still gets a
  // separate pin with its own reading date. Each item carries the readings shown in
  // the pin's info window and the data for its mini chart.
  geoMapItems: GeoMapItem[] = [];

  // Options for the mini line chart inside a map pin's info window (mirrors the
  // shared map favorite -- see map-favorite.component.ts).
  chartOptions: any = {
    showXAxis: true,
    showYAxis: true,
    gradient: false,
    showLegend: false,
    showXAxisLabel: false,
    xAxisLabel: 'Date-Time',
    showYAxisLabel: false,
    yAxisLabel: 'Value',
    autoScale: true,
    scaleYMin: null,
    scaleYMax: null,
    axisFormat: null,
    legendTitle: 'Sensors & Reading Types',
    timeline: false,
    colorScheme: {
      domain: [ '#046dd6', '#800080', '#42f55a', '#ffc107', '#fa0f0f' ]
    }
  };

  view: any[] = [255, 160];

  selectedDateRange: DateRange; // = { startDate: moment().startOf('day').subtract(1, 'days'), endDate: moment().endOf('day') };

  sensor: Sensor;
  selectedTimeZone: TimeZoneSelection;
  selectedTimeZoneOffset: number;
  selectedDefaultReadingType: string;

  showOriginalReadings: boolean = false;
  showCalibratedReadings: boolean = true;

  // eChart dark-background toggle (bound to the reusable eChart component).
  echartDarkBackground: boolean = false;
  private static readonly ECHART_DARK_BACKGROUND_STORAGE_KEY = 'EchartDarkBackground';

  // Source of the GPS coordinates currently in the readings, for the pin icon
  // (highest-priority source found wins):
  //  'reading'  - the reading's own gpsLocation (red)
  //  'readings' - derived from latitude/longitude reading types (blue)
  //  'sensor'   - copied from the sensor's own gpsLocation (green)
  //  'none'     - no GPS coordinates available (gray)
  gpsSource: 'reading' | 'readings' | 'sensor' | 'none' = 'none';

  sensorDataAvailabilityIsLoaded: boolean = false;

  sensorReadingInfosForPage: SensorReadingInfo[] = [];
  currentDataTablePageEvent: TableLazyLoadEvent = { first: 0, rows: 14 };
  currentDataTablePagePercent = 0;
  sensorReadingInfosCount = 0;

  sensorDataAvailability: Date[] = [];
  sensorDataAvailabilityReady = false;
  busyProcessing = false;

  sensorDataCols = [
    { field: 'createdDate', header: 'Time Stamp', width: '28%' },
    { field: 'source', header: 'Tag', width: '18%' },
    { field: 'dataType', header: 'Reading Type', width: '18%' },
    { field: 'origValue', header: 'Value (orig)', width: '15%' },
    { field: 'value', header: 'Value (cal)', width: '15%' },
    { field: 'gps', header: 'GPS', width: '6%' }
  ];

  sensorReadingTypeCols = [
    { field: 'checkbox', header: '', width: '15%' },
    { field: 'type', header: 'Reading Type', width: '65%' },
    { field: 'count', header: 'Cnt', width: '15%' }
  ];

  // Adapter inputs for the reusable eChart component (chart-readings-echart-modal).
  // Rebuilt from the single sensor / checked reading types whenever the selection
  // or data changes; kept as stable references between changes.
  public echartSensors: Sensor[] = [];
  public echartReadings: SensorReadingInfo[] = [];
  public echartReadingTypes: SensorReadingType[] = [];

  public availabilityChart: ChartInfo = {
    sensor: undefined,
    sensorName: '',
    sensorReadings: [],
    dataTypes: [],
    chartData: [],
    chartDays: 7,
    yMinScale: 0,
    yMaxScale: 1,
    yMinIgnore: undefined,
    yMaxIgnore: undefined,
    yMinData: undefined,
    yMaxData: undefined,
    view: [1500,80]
  };

  public availabilityChartOptions: any = {
    showXAxis: false,
    showYAxis: false,
    gradient: false,
    showLegend: false,
    animations: false,
    showXAxisLabel: false,
    xAxisLabel: '',
    showYAxisLabel: false,
    yAxisLabel:'',
    autoScale: true,
    colorScheme: {
      domain: [ '#046dd6', '#800080', '#42f55a', '#ffc107', '#fa0f0f' ]
    }
  };

  public tickCounter = 0;
  public tickInterval = 100;

  sensorDataParams: EventLogSearchCriteria;
  eventLogInfos: EventLogInfo[] = [];
  eventLogs: EventLog[] = [];
  ranges = DateRanges;
  invalidDates: moment.Moment[] = [];

  private svgDataAvailability;
  private dataAvailabilityMargin = 20;
  private dataAvailabilityWidth = 1455 - (this.dataAvailabilityMargin * 2);
  private dataAvailabilityHeight = 60 - (this.dataAvailabilityMargin * 2);

  public lat = 39.30;
  public lng = -97.90;
  public bounds?: google.maps.LatLngBounds;
  

  constructor(public bsModalRef: BsModalRef, private toastr: ToastrService, private modalService: BsModalService,
              @Inject(KIG_SETTINGS) private settings: KigSettings, public _sensorService: SensorService) {
  }


  ngOnInit(): void {
    this.getSensorReadingTypesForSensor(this.sensor, 'Loading Reading Types');
    this.loadSavedSettings();
  }


  setDragOn(drag: boolean) {
    this.modalDragOn = drag;

    if (drag) {
      setTimeout(() => {
        this.modalDragOn = false;
      },3000);
    }
  }


  loadSavedSettings() {
    var showOriginalReadingsStr = localStorage.getItem('ShowOriginalReadings');
    var showCalibratedReadingsStr = localStorage.getItem('ShowCalibratedReadings');
    var timeZoneSelectedStr = localStorage.getItem('TimeZoneSelected');
    
    [this.selectedTimeZone, this.selectedTimeZoneOffset] = this.common.setTimeZoneSelection(timeZoneSelectedStr);
    this.showCalibratedReadings = (showCalibratedReadingsStr === 'true') ? true : false;
    this.showOriginalReadings = (showOriginalReadingsStr === '' || showOriginalReadingsStr == undefined || showOriginalReadingsStr === 'true') ? true : false;

    this.echartDarkBackground = localStorage.getItem(SensorDataModalComponent.ECHART_DARK_BACKGROUND_STORAGE_KEY) === 'true';
  }


  // Persist the eChart dark-background preference when the user toggles it.
  onEchartDarkBackgroundChange() {
    localStorage.setItem(SensorDataModalComponent.ECHART_DARK_BACKGROUND_STORAGE_KEY, this.echartDarkBackground ? 'true' : 'false');
  }


  loadSensorDataAvailability() {
    this.getSensorDataAvailability(this.sensor);
  }


  getSensorReadingTypesForSensor(sensor: Sensor, loadingMessage: string): void {
    this._sensorService.getReadingTypesForSensors([sensor.id], loadingMessage).subscribe(
      results => {
        if (results != null) {
          sensor.sensorReadingTypes = results;
          this.common.sortArrayByTypeParm(sensor.sensorReadingTypes);

          sensor.sensorReadingTypeNames = (sensor.sensorReadingTypes != undefined) ? sensor.sensorReadingTypes.map(x => x.type).join(', ').toLowerCase() : undefined;
          if (sensor.sensorReadingTypes != undefined && sensor.sensorReadingTypes.length > 0) {
            this.selectDefaultSensorReadingType(sensor);
            //this.datesUpdated('');
          }
        } else {
          this.toastr.warning('No reading types found for ' + sensor.name);
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving sensor reading types');
      });
  }


  selectDefaultSensorReadingType(sensor: Sensor) {
    if (this.selectedDefaultReadingType == undefined || this.selectedDefaultReadingType == '') {
      sensor.sensorReadingTypes[0].checked = true;
      return;
    }

    var indexOfDefault = sensor.sensorReadingTypes.findIndex(x => x.type.toLowerCase() == this.selectedDefaultReadingType.toLowerCase());

    if (indexOfDefault > -1) {
      sensor.sensorReadingTypes[indexOfDefault].checked = true;
    }
    else {
      sensor.sensorReadingTypes[0].checked = true;
    }
  }


  getSensorDataAvailability(sensor: Sensor) {
    this.sensorDataAvailabilityIsLoaded = true;

    const sensorReadingCriteria: SensorReadingCriteria = {
      sensorId: sensor.id,
      startDate: moment().startOf('day').add(-5, 'years').format('YYYY-MM-DD HH:mm'), // moment().startOf('day').add(-5, 'years').toDate(),
      endDate: moment().endOf('day').format('YYYY-MM-DD HH:mm'),                      // moment().endOf('day').toDate(),
      timeZoneDisplay: this.selectedTimeZone,
      timeZoneOffset: this.selectedTimeZoneOffset
    };

    this._sensorService.getSensorDataAvailability(sensorReadingCriteria, '').subscribe(
      results => {
        if (results != null) {
          this.sensorDataAvailability = results;
          //this.sensorDataAvailability = this.sensorDataAvailability.map(x => new Date(moment.utc(x).endOf('day').local().toDate()));
          this.sensorDataAvailability = this.sensorDataAvailability.map(x => new Date(x));
          this.sensorDataAvailabilityReady = true;
          this.updateDataAvailabilityChart(this.sensorDataAvailability);
        } else {
          this.toastr.warning('No sensor data found for ' + sensor.name);
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving sensor data availability');
      });
  }


  getSensorReadings(sensor: Sensor, selectedDateRange: DateRange ) {
    const sensorReadingCriteria: SensorReadingCriteria2 = {
      sensorIds: [sensor.id],
      startDate: selectedDateRange.startDate.startOf('day').format('YYYY-MM-DD HH:mm'), // selectedDateRange.startDate.startOf('day').toDate(),
      endDate: selectedDateRange.endDate.endOf('day').format('YYYY-MM-DD HH:mm'), // selectedDateRange.endDate.endOf('day').toDate(),
      timeZoneDisplay: this.selectedTimeZone,
      timeZoneOffset: this.selectedTimeZoneOffset
    };

    this._sensorService.getSensorReadingsForSensor(sensorReadingCriteria).subscribe(
      results => {
        if (results != null) {
          this.sensor.sensorReadingInfos = results;
          this.sensor.sensorReadingInfos = this.sensor.sensorReadingInfos.filter(s => s.value != 'nan' && s.value != 'NaN' );
          //this.sensor.sensorReadingInfos.forEach(x => x.createdDate = new Date(moment.utc(x.createdDate).local().toDate()));
          this.sensor.sensorReadingInfos.forEach(x => x.createdDate = new Date(x.createdDate));
          this.sensor.sensorReadingTypes.forEach(x => x.count = this.sensor.sensorReadingInfos.filter(y => y.typeID == x.id).length );

          this.sensor.sensorReadingInfos.sort((a, b) => {
            if (b.createdDate > a.createdDate) {
              return 1;
            }
            if (b.createdDate < a.createdDate) {
              return -1;
            }
          });

          this.backfillMissingGpsLocations();

          this.sensorReadingSelectionChanged();
        } else {
          this.toastr.warning('No sensor data found for ' + sensor.name);
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving sensor data availability');
      });
  }


  isInvalidDate = (m: moment.Moment) => this.invalidDates.some(d => d.isSame(m, 'day'));

  datesUpdated(quickPick: string): void {
    if (quickPick === 'Today') {
      this.selectedDateRange = { startDate: moment().startOf('day'), endDate: moment().endOf('day') };
    } else if (quickPick === 'Last 2 Days') {
      this.selectedDateRange = { startDate: moment().startOf('day').subtract(1, 'days'), endDate: moment().endOf('day') };
    } else if (quickPick === 'Last 7 Days') {
      this.selectedDateRange = { startDate: moment().startOf('day').subtract(6, 'days'), endDate: moment().endOf('day') };
    } else if (quickPick === 'Last 30 Days') {
      this.selectedDateRange = { startDate: moment().startOf('day').subtract(29, 'days'), endDate: moment().endOf('day') };
    }

    this.getSensorReadings(this.sensor, this.selectedDateRange);
    this.selectedDateRange = this.selectedDateRange;    // needed to wake up angular so data gets sent to child component
  }


  toggleCheckedSensorReadingTypes() {
    const newState = !this.sensor.sensorReadingTypes[0].checked;
    this.sensor.sensorReadingTypes.forEach(x => x.checked = newState);
    this.sensorReadingSelectionChanged();
  }


  sensorReadingSelectionChanged() {
    this.updateGeoMap();
    this.updateEchartInputs();

    this.currentDataTablePageEvent.first = 0;
    this.updateDataTable(this.currentDataTablePageEvent);
    this.sensor.sensorReadingTypes = [...this.sensor.sensorReadingTypes];  // Needed to give Angular a kick to pass new values to child component

    if (this.exportDataModal != undefined) {
      this.exportDataModal.startUp();
    }
  }


  // Map the single sensor and its checked reading types onto the array inputs
  // expected by the reusable eChart component. New array references are assigned
  // so Angular passes the updated values to the child.
  updateEchartInputs() {
    if (this.sensor == undefined) {
      this.echartSensors = [];
      this.echartReadings = [];
      this.echartReadingTypes = [];
      return;
    }

    this.echartSensors = [this.sensor];
    this.echartReadings = this.sensor.sensorReadingInfos != undefined ? [...this.sensor.sensorReadingInfos] : [];
    this.echartReadingTypes = (this.sensor.sensorReadingTypes != undefined)
      ? this.sensor.sensorReadingTypes.filter(x => x.checked)
      : [];
  }


  // True when a reading has usable (non-0/0) GPS coordinates.
  private readingHasGps(r: SensorReadingInfo): boolean {
    return r.gpsLocation != undefined
      && (Math.abs(r.gpsLocation.gpsLat) > 0 || Math.abs(r.gpsLocation.gpsLong) > 0);
  }

  // Resolve each reading's GPS by priority and tag it (and set gpsSource for the
  // indicator icon to the highest-priority source found):
  //   1. latitude/longitude reading types (same timestamp) -> 'readings'
  //   2. the reading's own gpsLocation           -> 'reading'
  //   3. the sensor's own gpsLocation             -> 'sensor'
  //   4. none                                     -> 'none'
  // Coordinates from sources 1 and 3 are backfilled onto the reading.
  backfillMissingGpsLocations() {
    this.gpsSource = 'none';

    if (this.sensor == undefined || this.sensor.sensorReadingInfos == undefined) {
      return;
    }

    const latTypes = ['latitude', 'lat'];
    const longTypes = ['longitude', 'long', 'lng'];

    // Index latitude / longitude values by their timestamp (ms).
    const latByTime = new Map<number, number>();
    const longByTime = new Map<number, number>();

    this.sensor.sensorReadingInfos.forEach(r => {
      const type = (r.type != undefined) ? r.type.toLowerCase() : '';
      const value = Number(r.value);
      if (isNaN(value)) {
        return;
      }
      const key = new Date(r.createdDate).getTime();
      if (latTypes.includes(type)) {
        latByTime.set(key, value);
      } else if (longTypes.includes(type)) {
        longByTime.set(key, value);
      }
    });

    // Sensor's own gpsLocation for source #3.
    const sensorGps = (this.sensor.gpsLocation != undefined
        && (Math.abs(this.sensor.gpsLocation.gpsLat) > 0 || Math.abs(this.sensor.gpsLocation.gpsLong) > 0))
      ? this.sensor.gpsLocation
      : undefined;

    const priority = { readings: 3, reading: 2, sensor: 1, none: 0 };
    let best: 'reading' | 'readings' | 'sensor' | 'none' = 'none';

    this.sensor.sensorReadingInfos.forEach(r => {
      let source: 'reading' | 'readings' | 'sensor' | 'none';

      // Remember the GPS the reading itself carries before steps 1 and 3 overwrite gpsLocation.
      // First pass only: gpsSource is undefined until a reading has been backfilled once.
      if (r.gpsSource === undefined && this.readingHasGps(r)) {
        r.ownGpsLocation = r.gpsLocation;
      }

      const key = new Date(r.createdDate).getTime();
      const lat = latByTime.get(key);
      const long = longByTime.get(key);

      if (lat != undefined || long != undefined) {
        // 1. latitude/longitude reading types
        r.gpsLocation = { id: 0, gpsLat: lat ?? 0, gpsLong: long ?? 0, gpsAlt: 0, locationDescription: '' };
        source = 'readings';
      } else if (this.readingHasGps(r)) {
        // 2. the reading's own gpsLocation
        source = 'reading';
      } else if (sensorGps != undefined) {
        // 3. the sensor's own gpsLocation
        r.gpsLocation = { ...sensorGps };
        source = 'sensor';
      } else {
        // 4. none
        source = 'none';
      }

      r.gpsSource = source;
      if (priority[source] > priority[best]) {
        best = source;
      }
    });

    this.gpsSource = best;
  }


  updateGeoMap() {
    const gpsInfo = this.getGeoMapCenter(this.sensor.sensorReadingInfos);
    const lat = Math.abs(gpsInfo.gpsLat) > 1 ? gpsInfo.gpsLat : 41.67528016778091;
    const long = Math.abs(gpsInfo.gpsLong) > 1 ? gpsInfo.gpsLong : -93.72057098707288;

    this.mapCenter = {
      lat,
      lng: long,
    };

    this.geoMapItems = [];
    this.common.clearAllPinPopupLeaders();   // drop leaders from the prior marker set

    // Only the reading types the user has checked, matching the Table and Chart
    // tabs. Readings are already sorted newest-first by getSensorReadings().
    const checkedTypeIds = (this.sensor.sensorReadingTypes != undefined)
      ? this.sensor.sensorReadingTypes.filter(x => x.checked).map(x => x.id)
      : [];

    const readings = this.sensor.sensorReadingInfos.filter(x =>
      x.gpsLocation != undefined && checkedTypeIds.includes(x.typeID));

    // Group consecutive readings at the same coordinates into one "visit", so a
    // sensor that returns to an earlier location gets its own pin per visit.
    let currentGroup: SensorReadingInfo[] = [];
    const visits: SensorReadingInfo[][] = [];

    readings.forEach(reading => {
      const previous = currentGroup[currentGroup.length - 1];
      const sameLocation = previous != undefined
        && previous.gpsLocation.gpsLat === reading.gpsLocation.gpsLat
        && previous.gpsLocation.gpsLong === reading.gpsLocation.gpsLong;

      if (sameLocation) {
        currentGroup.push(reading);
      }
      else {
        if (currentGroup.length > 0) {
          visits.push(currentGroup);
        }
        currentGroup = [reading];
      }
    });

    if (currentGroup.length > 0) {
      visits.push(currentGroup);
    }

    const sensorName = this.common.getSensorDisplayName(this.sensor);

    visits.forEach(visit => {
      const first = visit[0];

      this.geoMapItems.push({
        gpsLong: first.gpsLocation.gpsLong,
        gpsLat: first.gpsLocation.gpsLat,
        gpsAlt: first.gpsLocation.gpsAlt,
        description: first.gpsLocation.locationDescription,
        sensorRef: sensorName,
        labelOptions: {
          color: 'black',
          fontFamily: '',
          fontSize: '14px',
          fontWeight: 'bold',
          text: ' '
        },
        readingTypeCharted: '',
        readingDate: moment(first.createdDate).toDate(),
        readings: this.buildReadingsDictionary(visit, first.gpsLocation),
        chartData: [],
        iconUrl: '',
        icon: this.common.gpsPinIcon(first.gpsSource ?? 'reading'),
        headerColors: this.common.gpsPinHeaderColors(first.gpsSource ?? 'reading')
      });
    });

    this.geoMapItems = [...this.geoMapItems];

    this.fitMapToMarkers();
  }


  // Reading type -> value for a pin's info window, plus the pin's coordinates.
  // Mirrors buildReadingsDictionary() in map-favorite.component.ts.
  buildReadingsDictionary(readings: SensorReadingInfo[], gpsCoordinates: GpsInfo): { [key: string]: string } {
    const readingsDictionary: { [key: string]: string } = {};

    readings.forEach(reading => {
      if (reading.type != undefined && reading.value != undefined) {
        readingsDictionary[reading.type] = Number(reading.value).toPrecision(9);
      }
    });

    if (gpsCoordinates != undefined) {
      readingsDictionary['gpsLat'] = gpsCoordinates.gpsLat.toString();
      readingsDictionary['gpsLong'] = gpsCoordinates.gpsLong.toString();
    }

    return readingsDictionary;
  }


  // Chart the clicked reading type over the whole loaded date range (not just the
  // pin's visit), so the mini chart gives the reading's context. Mirrors
  // selectReadingType() / chartData() in map-favorite.component.ts.
  loadChartReadings(geoMapItem: GeoMapItem, readingTypeName: string): void {
    geoMapItem.readingTypeCharted = readingTypeName;

    geoMapItem.chartData = [];

    if (this.sensor == undefined || this.sensor.sensorReadingInfos == undefined) {
      return;
    }

    const series = this.sensor.sensorReadingInfos
      .filter(s => s.type == readingTypeName && s.value != 'nan' && s.value != 'NaN')
      .map(s => ({ name: new Date(s.createdDate), value: s.value }));

    if (series.length > 0) {
      geoMapItem.chartData = [
        { name: readingTypeName + ', ' + this.common.getSensorDisplayName(this.sensor), series: series }
      ];
    }
  }


  // Zoom/center the map so all pins are visible. Uses fitBounds for multiple
  // distinct pins; centers at a capped zoom for a single (or identical) pin.
  private fitMapToMarkers() {
    // Defer so the map instance and marker bindings have settled.
    setTimeout(() => {
      if (this.map == undefined || this.map.googleMap == undefined || this.geoMapItems.length === 0) {
        return;
      }

      const bounds = new google.maps.LatLngBounds();
      this.geoMapItems.forEach(m => bounds.extend(new google.maps.LatLng(m.gpsLat, m.gpsLong)));

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const singleLocation = ne.lat() === sw.lat() && ne.lng() === sw.lng();

      if (singleLocation) {
        this.map.googleMap.setCenter(bounds.getCenter());
        this.map.googleMap.setZoom(15);
      } else {
        this.map.googleMap.fitBounds(bounds);
      }
    });
  }


  getRandomColor() {
    const color = Math.floor(Math.random() * 16777216).toString(16);
    return '000000'.slice(0, -color.length) + color;
  }


  getGeoMapCenter(readings: SensorReadingInfo[]): GpsInfo {
    const gpsInfo: GpsInfo = {
      id: 0,
      gpsLong: 0,
      gpsLat: 0,
      gpsAlt: 0,
      locationDescription: ''
    };

    if (readings == undefined || readings.length < 1) {
      return gpsInfo;
    }

    const firstWithGps = readings.find(r => r.gpsLocation != undefined);
    if (firstWithGps != undefined) {
      gpsInfo.gpsLat = firstWithGps.gpsLocation.gpsLat;
      gpsInfo.gpsLong = firstWithGps.gpsLocation.gpsLong;
    }

    return gpsInfo;
  }


  updateDataAvailabilityChart(sensorDataDates: Date[]) {
    this.createSvgForDataAvailability();
    this.drawDataAvailabilityBars();
  }


  updateDataTable(tablePageEvent: TableLazyLoadEvent) {
    this.currentDataTablePageEvent = tablePageEvent;

    if (this.sensor.sensorReadingInfos == undefined || this.sensor.sensorReadingInfos.length < 1) return;

    this.sensorReadingInfosForPage = JSON.parse(JSON.stringify(this.sensor.sensorReadingInfos));
    this.sensorReadingInfosForPage.forEach(x => x.createdDate = moment(x.createdDate).toDate());

    const checkedReadingTypes = this.sensor.sensorReadingTypes.filter(x => x.checked).map(y => y.id);
    this.sensorReadingInfosForPage = this.sensorReadingInfosForPage.filter(x => checkedReadingTypes.includes(x.typeID));
    this.sensorReadingInfosCount = this.sensorReadingInfosForPage.length;
    this.sensorReadingInfosForPage = this.sensorReadingInfosForPage.slice(tablePageEvent.first, tablePageEvent.first + tablePageEvent.rows);

    let compareDate = moment().local().toDate();

    for (let index=0; index < this.sensorReadingInfosForPage.length; index++) {
      const s = this.sensorReadingInfosForPage[index];
      if (s.createdDate != undefined && s.createdDate.getTime() === compareDate.getTime()) {
        s.createdDate = undefined;
        s.tag = '';
      } else if (s.createdDate != undefined) {
        compareDate = s.createdDate;
      }
    }
  }


  private createSvgForDataAvailability(): void {
    this.svgDataAvailability = d3.select('figure#bar')
      .append('svg')
      .attr('width', this.dataAvailabilityWidth + (this.dataAvailabilityMargin * 2))
      .attr('height', this.dataAvailabilityHeight + (this.dataAvailabilityMargin * 2))
      .append('g')
      .attr('transform', `translate(${this.dataAvailabilityMargin},${this.dataAvailabilityMargin})`);
  }


  private convertDataToDate(data): Date {
    const year = Math.floor(data);
    const dayOfYearAsFraction = data - year;
    const dayOfYear = dayOfYearAsFraction * 365;
    return new Date(year, 0, dayOfYear);
  }


  private drawDataAvailabilityBars(): void {
    const yearData = this.sensorDataAvailability.map(x => Number(moment(x).format('YYYY')) + moment(x).dayOfYear()/365);
    const years = this.sensorDataAvailability.map(x => Number(moment(x).format('YYYY')));
    const minYear = Math.floor(Math.min(...years));
    const maxYear = Math.floor(Math.max(...years)) + 1;

    // Add X axis
    const x = d3.scaleLinear()
      .domain([minYear, maxYear])
      .range([ 0, this.dataAvailabilityWidth ]);

    this.svgDataAvailability.append('g')
      .attr('transform', `translate(${0},${this.dataAvailabilityHeight})`)
      .call(d3.axisBottom(x).tickFormat(d3.format('d')).ticks(maxYear - minYear));

    // Add Y axis
    const y = d3.scaleLinear()
      .domain([0, 2])
      .range([ this.dataAvailabilityHeight, 0]);

    const div = d3.select('figure#bar').append('div')
      .attr('class', 'tooltip')
      .style('opacity', 0);


    // Create and fill the bars
    this.svgDataAvailability.selectAll('bars')
      .data(yearData)
      .enter()
      .append('rect')
      .attr('x', d => x(d))
      .attr('y', d => y(2))
      .attr('width', this.dataAvailabilityWidth/((maxYear - minYear + 1) * 365) * 2)
      .attr('height', (d) => this.dataAvailabilityHeight - y(1.8))
      .attr('fill', '#548235')
      .attr('text-anchor', 'top')
      .on('mouseover', (event,d) => {
        div.transition()
          .duration(200)
          .style('opacity', .9);
        div.html(() => {
          const year = Math.floor(d);
          const dayOfYearAsFraction = d - year;
          const dayOfYear = dayOfYearAsFraction * 365;
          const date = new Date(year, 0, dayOfYear);
          return moment(date).format('YYYY-MM-DD');
        })
          .style('left', x(d) + 'px')
          .style('top', 30 + 'px')
          .style('position', 'absolute')
          .style('font-size', 13)
          .style('padding', 1 + 'px');
      })
      .on('mouseout', (d) => {
        div.transition()
          .duration(500)
          .style('opacity', 0);
      })
      .on('mousedown', (event,d) => {
        console.log(event, d)
        selectedRects.startRect = event.target;
      })
      .on('mouseup', (event,d) => {
        if (selectedRects.startRect && selectedRects.startRect.__data__ <= event.target.__data__){
          selectedRects.endRect = event.target;
          this.selectedDateRange = {startDate : moment(this.convertDataToDate(selectedRects.startRect.__data__)), endDate : moment(this.convertDataToDate(selectedRects.endRect.__data__))};
          this.svgDataAvailability.selectAll('rect')
            .filter( function(d) {
              if (d >= selectedRects.startRect.__data__ && d <= selectedRects.endRect.__data__){
                this.style.fill = 'brown';
              } else {
                this.style.fill = '#548235';
              }
              return;
            });
          this.getSensorReadings(this.sensor, this.selectedDateRange);
        }
      })
      .style('cursor', 'pointer');
  }

  selectedTimeZoneChanged() {
    switch (this.selectedTimeZone) {
      case TimeZoneSelection.Local:
        localStorage.setItem('TimeZoneSelected', 'Local');
        this.selectedTimeZoneOffset = moment().utcOffset();
        break;
    
    case TimeZoneSelection.UTC:
      localStorage.setItem('TimeZoneSelected', 'UTC');
        this.selectedTimeZoneOffset = 0;
        break;

    case TimeZoneSelection.Source:
      localStorage.setItem('TimeZoneSelected', 'Source');
        this.selectedTimeZoneOffset = 0;
        break;

      default:
        this.selectedTimeZoneOffset = 0;
        break;
    }

    this.datesUpdated("");
  }


  selectedReadingSourceChanged() {
    localStorage.setItem('ShowOriginalReadings', this.showOriginalReadings.toString());
    localStorage.setItem('ShowCalibratedReadings', this.showCalibratedReadings.toString());
    
    this.sensorReadingSelectionChanged();
  }


}
